import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { WorkOrderPoProcessType } from "api/models/request/work-order-po/work-order-po-process-update";
import { WorkOrdersPoProcessPermission } from "commons/enum/permission";
import { dateTimeDisplay } from "commons/ultilities/date/dayjs";
import { observer } from "mobx-react-lite";
import { IPackageProgressModel } from "mobx-stores/_models/work-order-po/progress/progress-models";
import { IWorkOrderPOProgressModel } from "mobx-stores/_models/work-order-po/progress/work-order-po-progress-base-model";
import { useStores } from "mobx-stores/_root-store";
import { ReactNode, useCallback, useState } from "react";
import { NumberText } from "share-components/atomics/number-text";
import {
  EditIconButton,
  ExpandButton,
} from "share-components/mui-wrap/button/action-button";
import { XGroupCell } from "share-components/mui-wrap/table/x-group-cell";
import { XSubRow } from "share-components/mui-wrap/table/x-sub-row";
import { XTablePagination } from "share-components/mui-wrap/table/x-table-pagination";
import { EditProcessDialog } from "./edit-process-dialog";
import { AcceptanceProcessTable } from "./acceptance-process-table";
import { PrintProcessTable } from "./print-process-table";
import { StandardProcessTable } from "./standard-process-table";
import { ProcessWithPrintAmountTable } from "./standard-process-with-print-amount-table";
import OrderNumberBadge from "./order-number-badge";

export const WorkOrderProcess = observer(() => {
  const {
    workOrderPoManagement: { getWorkOrderPoDetail, workOrderPoDetail },
  } = useStores();
  const [openEditProcess, setOpenEditProcess] = useState(false);
  const [selectedProcessType, setSelectedProcessType] =
    useState<WorkOrderPoProcessType>("paperOutput");

  const toNullableProgress = <T extends IWorkOrderPOProgressModel>(
    value: T | undefined,
  ): T | null => {
    return value === undefined ? null : value;
  };

  const toNullablePackageProgress = (
    value: IPackageProgressModel | undefined,
  ): IPackageProgressModel => {
    return value === undefined ? null : value;
  };

  const paperOutputProgress = toNullableProgress(
    workOrderPoDetail?.paperOutputProgress,
  );
  const printProgress = toNullableProgress(workOrderPoDetail?.printProgress);
  const matteProgress = toNullableProgress(workOrderPoDetail?.matteProgress);
  const acceptanceProgress = toNullableProgress(
    workOrderPoDetail?.acceptanceProgress,
  );
  const foilProgress = toNullableProgress(workOrderPoDetail?.foilProgress);
  const shapeProgress = toNullableProgress(workOrderPoDetail?.shapeProgress);
  const pasteProgress = toNullableProgress(workOrderPoDetail?.pasteProgress);
  const cutProgress = toNullableProgress(workOrderPoDetail?.cutProgress);
  const packageProgress = toNullablePackageProgress(
    workOrderPoDetail?.packageProgress,
  );

  const openProcessEditHandler = useCallback((type: WorkOrderPoProcessType) => {
    setSelectedProcessType(type);
    setOpenEditProcess(true);
  }, []);

  const closeProcessEditHandler = useCallback(() => {
    setOpenEditProcess(false);
  }, []);

  const onProcessSaved = useCallback(async () => {
    if (!workOrderPoDetail?.id) return;
    await getWorkOrderPoDetail(workOrderPoDetail.id);
  }, [getWorkOrderPoDetail, workOrderPoDetail?.id]);

  const selectedProgress: IWorkOrderPOProgressModel = (() => {
    switch (selectedProcessType) {
      case "paperOutput":
        return paperOutputProgress;
      case "print":
        return printProgress;
      case "matte":
        return matteProgress;
      case "acceptance":
        return acceptanceProgress;
      case "foil":
        return foilProgress;
      case "shape":
        return shapeProgress;
      case "paste":
        return pasteProgress;
      case "cut":
        return cutProgress;
      case "package":
        return packageProgress;
    }
  })();

  return (
    <>
      <Stack spacing={2}>
        <PaperStandardProcess
          progress={paperOutputProgress}
          note={workOrderPoDetail?.paperOutputStandardDisplay.note ?? ""}
          onEdit={() => openProcessEditHandler("paperOutput")}
          editAccessPermission={[
            WorkOrdersPoProcessPermission.Default,
            WorkOrdersPoProcessPermission.PaperOutput,
          ]}
          leadingCell={
            workOrderPoDetail?.paperOutputStandardDisplay.standard ?? ""
          }
          orderNumber={1}
        ></PaperStandardProcess>

        <PrintProcessTable
          standardTitle="Tiêu Chuẩn In Ấn"
          departmentTitle="Bộ Phận In Ấn (ĐVT: tờ in)"
          progress={printProgress}
          note={workOrderPoDetail?.printStandardDisplay.note ?? ""}
          onEdit={() => openProcessEditHandler("print")}
          editAccessPermission={[
            WorkOrdersPoProcessPermission.Default,
            WorkOrdersPoProcessPermission.Print,
          ]}
          leadingCell={workOrderPoDetail?.printStandardDisplay.standard ?? ""}
          orderNumber={2}
        ></PrintProcessTable>

        <CoatingStandardProcess
          progress={matteProgress}
          note={workOrderPoDetail?.matteStandardDisplay.note ?? ""}
          onEdit={() => openProcessEditHandler("matte")}
          editAccessPermission={[
            WorkOrdersPoProcessPermission.Default,
            WorkOrdersPoProcessPermission.Matte,
          ]}
          leadingCell={workOrderPoDetail?.matteStandardDisplay.standard ?? ""}
          orderNumber={3}
        ></CoatingStandardProcess>

        <AcceptanceProcessTable
          standardTitle="Tiêu Chuẩn Nghiệm Thu"
          departmentTitle="Nghiệm Thu Hàng Sau In (ĐVT: tờ in + pcs)"
          progress={acceptanceProgress}
          note={workOrderPoDetail?.acceptanceStandardDisplay.note ?? ""}
          onEdit={() => openProcessEditHandler("acceptance")}
          editAccessPermission={[
            WorkOrdersPoProcessPermission.Default,
            WorkOrdersPoProcessPermission.Acceptance,
          ]}
          leadingCell={
            workOrderPoDetail?.acceptanceStandardDisplay.standard ?? ""
          }
          orderNumber={4}
        ></AcceptanceProcessTable>

        <ProcessingStandardProcess
          progress={foilProgress}
          note={workOrderPoDetail?.foilStandardDisplay.note ?? ""}
          onEdit={() => openProcessEditHandler("foil")}
          editAccessPermission={[
            WorkOrdersPoProcessPermission.Default,
            WorkOrdersPoProcessPermission.Foil,
          ]}
          leadingCell={workOrderPoDetail?.foilStandardDisplay.standard ?? ""}
          orderNumber={5}
        ></ProcessingStandardProcess>

        <ShapingStandardProcess
          progress={shapeProgress}
          note={workOrderPoDetail?.shapeStandardDisplay.note ?? ""}
          onEdit={() => openProcessEditHandler("shape")}
          editAccessPermission={[
            WorkOrdersPoProcessPermission.Default,
            WorkOrdersPoProcessPermission.Shape,
          ]}
          leadingCell={workOrderPoDetail?.shapeStandardDisplay.standard ?? ""}
          orderNumber={6}
        ></ShapingStandardProcess>

        <CuttingStandardProcess
          progress={cutProgress}
          note={workOrderPoDetail?.cutStandardDisplay.note ?? ""}
          onEdit={() => openProcessEditHandler("cut")}
          editAccessPermission={[
            WorkOrdersPoProcessPermission.Default,
            WorkOrdersPoProcessPermission.Cut,
          ]}
          leadingCell={workOrderPoDetail?.cutStandardDisplay.standard ?? ""}
          orderNumber={7}
        ></CuttingStandardProcess>

        <PastingStandardProcess
          progress={pasteProgress}
          note={workOrderPoDetail?.pasteStandardDisplay.note ?? ""}
          onEdit={() => openProcessEditHandler("paste")}
          editAccessPermission={[
            WorkOrdersPoProcessPermission.Default,
            WorkOrdersPoProcessPermission.Paste,
          ]}
          leadingCell={workOrderPoDetail?.pasteStandardDisplay.standard ?? ""}
          orderNumber={8}
        ></PastingStandardProcess>

        <PackingStandardProcess
          progress={packageProgress}
          note={workOrderPoDetail?.packageStandardDisplay.note ?? ""}
          onEdit={() => openProcessEditHandler("package")}
          editAccessPermission={[
            WorkOrdersPoProcessPermission.Default,
            WorkOrdersPoProcessPermission.Package,
          ]}
          leadingCell={workOrderPoDetail?.packageStandardDisplay.standard ?? ""}
          orderNumber={9}
        ></PackingStandardProcess>
      </Stack>

      {!!workOrderPoDetail?.id && (
        <EditProcessDialog
          key={selectedProcessType}
          open={openEditProcess}
          processType={selectedProcessType}
          workOrderPoId={workOrderPoDetail.id}
          progress={selectedProgress}
          packageProgress={packageProgress}
          onClose={closeProcessEditHandler}
          onSaved={onProcessSaved}
        ></EditProcessDialog>
      )}
    </>
  );
});

const PaperStandardProcess = observer(
  ({
    progress,
    note,
    onEdit,
    editAccessPermission,
    leadingCell,
    orderNumber,
  }: {
    progress: IWorkOrderPOProgressModel;
    note: string;
    onEdit: () => void;
    editAccessPermission: string[];
    orderNumber: number;
    leadingCell?: ReactNode;
  }) => {
    return (
      <StandardProcessTable
        standardTitle="Tiêu Chuẩn Xuất Giấy"
        departmentTitle="Bộ Phận Xuất Giấy (ĐVT: tờ in)"
        progress={progress}
        note={note}
        onEdit={onEdit}
        editAccessPermission={editAccessPermission}
        leadingCell={leadingCell}
        orderNumber={orderNumber}
      ></StandardProcessTable>
    );
  },
);

const CoatingStandardProcess = observer(
  ({
    progress,
    note,
    onEdit,
    editAccessPermission,
    leadingCell,
    orderNumber,
  }: {
    progress: IWorkOrderPOProgressModel;
    note: string;
    onEdit: () => void;
    editAccessPermission: string[];
    orderNumber: number;
    leadingCell?: ReactNode;
  }) => {
    return (
      <StandardProcessTable
        standardTitle="Tiêu Chuẩn X/L Láng"
        departmentTitle="Bộ Phận X/L Láng (ĐVT: tờ in)"
        progress={progress}
        note={note}
        onEdit={onEdit}
        editAccessPermission={editAccessPermission}
        leadingCell={leadingCell}
        orderNumber={orderNumber}
      ></StandardProcessTable>
    );
  },
);

const ProcessingStandardProcess = observer(
  ({
    progress,
    note,
    onEdit,
    editAccessPermission,
    leadingCell,
    orderNumber,
  }: {
    progress: IWorkOrderPOProgressModel;
    note: string;
    onEdit: () => void;
    editAccessPermission: string[];
    orderNumber: number;
    leadingCell?: ReactNode;
  }) => {
    return (
      <ProcessWithPrintAmountTable
        standardTitle="Tiêu Chuẩn Xử Lý"
        departmentTitle="Ép Nhũ - Khiếm Thị - Hiệu Ứng (ĐVT: tờ in + pcs)"
        progress={progress}
        note={note}
        onEdit={onEdit}
        editAccessPermission={editAccessPermission}
        leadingCell={leadingCell}
        orderNumber={orderNumber}
      ></ProcessWithPrintAmountTable>
    );
  },
);

const ShapingStandardProcess = observer(
  ({
    progress,
    note,
    onEdit,
    editAccessPermission,
    leadingCell,
    orderNumber,
  }: {
    progress: IWorkOrderPOProgressModel;
    note: string;
    onEdit: () => void;
    editAccessPermission: string[];
    orderNumber: number;
    leadingCell?: ReactNode;
  }) => {
    return (
      <ProcessWithPrintAmountTable
        standardTitle="Tiêu Chuẩn Định Hình"
        departmentTitle="Bộ Phận Bế Định Hình (ĐVT: tờ in + pcs)"
        progress={progress}
        note={note}
        onEdit={onEdit}
        editAccessPermission={editAccessPermission}
        leadingCell={leadingCell}
        orderNumber={orderNumber}
      ></ProcessWithPrintAmountTable>
    );
  },
);

const PastingStandardProcess = observer(
  ({
    progress,
    note,
    onEdit,
    editAccessPermission,
    leadingCell,
    orderNumber,
  }: {
    progress: IWorkOrderPOProgressModel;
    note: string;
    onEdit: () => void;
    editAccessPermission: string[];
    orderNumber: number;
    leadingCell?: ReactNode;
  }) => {
    return (
      <StandardProcessTable
        standardTitle="Tiêu Chuẩn Dán T/P"
        departmentTitle="Bộ Phận Dán Thành Phẩm (ĐVT: pcs)"
        progress={progress}
        note={note}
        onEdit={onEdit}
        editAccessPermission={editAccessPermission}
        leadingCell={leadingCell}
        orderNumber={orderNumber}
      ></StandardProcessTable>
    );
  },
);

const CuttingStandardProcess = observer(
  ({
    progress,
    note,
    onEdit,
    editAccessPermission,
    leadingCell,
    orderNumber,
  }: {
    progress: IWorkOrderPOProgressModel;
    note: string;
    onEdit: () => void;
    editAccessPermission: string[];
    orderNumber: number;
    leadingCell?: ReactNode;
  }) => {
    return (
      <StandardProcessTable
        standardTitle="Tiêu Chuẩn Cắt T/P"
        departmentTitle="Bộ Phận Cắt Thành Phẩm (ĐVT: pcs)"
        progress={progress}
        note={note}
        onEdit={onEdit}
        editAccessPermission={editAccessPermission}
        leadingCell={leadingCell}
        orderNumber={orderNumber}
      ></StandardProcessTable>
    );
  },
);

const PackingStandardProcess = observer(
  ({
    progress,
    note,
    onEdit,
    editAccessPermission,
    leadingCell,
    orderNumber,
  }: {
    progress: IPackageProgressModel;
    note: string;
    onEdit: () => void;
    editAccessPermission: string[];
    orderNumber: number;
    leadingCell?: ReactNode;
  }) => {
    const [open, setOpen] = useState(false);

    return (
      <OrderNumberBadge orderNumber={orderNumber}>
        <XTablePagination hidePagination>
          <TableHead>
            <TableRow>
              <TableCell align="center" rowSpan={2}>
                Tiêu Chuẩn Đóng Gói
              </TableCell>
              <TableCell align="center" colSpan={9}>
                Bộ Phận Đóng Gói Thành Phẩm (ĐVT: pcs)
              </TableCell>
              <TableCell align="center" rowSpan={2}>
                Ghi Chú Sản Xuất
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Ngày S/X</TableCell>
              <TableCell>Số Lượng S/X</TableCell>
              <TableCell> Thiết Bị S/X</TableCell>
              <TableCell> Người S/X</TableCell>
              <TableCell>Người Kiểm QT</TableCell>
              <TableCell>Ngày H/T</TableCell>
              <TableCell>Số Lượng H/T</TableCell>
              <TableCell>Hao Hụt</TableCell>
              <TableCell>Tỉ Lệ %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow hover>
              <TableCell sx={{ width: "30px" }}>
                <Stack direction="row" spacing={0.5}>
                  <ExpandButton
                    onClick={() => setOpen(!open)}
                    open={open}
                  ></ExpandButton>
                  {leadingCell}
                  <EditIconButton
                    accessPermission={editAccessPermission}
                    onClick={onEdit}
                  ></EditIconButton>
                </Stack>
              </TableCell>
              <TableCell>{dateTimeDisplay(progress?.date) ?? ""}</TableCell>
              <TableCell>
                <NumberText value={progress?.amount ?? 0}></NumberText>
              </TableCell>
              <TableCell>{progress?.equipment ?? ""}</TableCell>
              <TableCell>{progress?.operator ?? ""}</TableCell>
              <TableCell>{progress?.inspector ?? ""}</TableCell>
              <TableCell>
                {dateTimeDisplay(progress?.completedDate) ?? ""}
              </TableCell>
              <TableCell>
                <NumberText value={progress?.completedAmount ?? 0}></NumberText>
              </TableCell>
              <TableCell>
                <NumberText
                  value={
                    progress?.lossAmount ?? progress?.lossAmountFECalc ?? 0
                  }
                ></NumberText>
              </TableCell>
              <TableCell>
                <NumberText
                  value={
                    progress?.lossPercent ?? progress?.lossPercentFECalc ?? 0
                  }
                ></NumberText>
              </TableCell>
              <TableCell>{note}</TableCell>
            </TableRow>
            {open && (
              <XSubRow>
                <XGroupCell colSpan={11} headerTitle="Chi Tiết Đóng Gói:">
                  <Table>
                    <TableHead
                      sx={{
                        "tr > th": { backgroundColor: "#FDE9D9 !important" },
                      }}
                    >
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          Chi Tiết Đóng Gói
                        </TableCell>
                        <TableCell rowSpan={2}>Ghi chú</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Số Kiện Chẵn</TableCell>
                        <TableCell>SL/ Kiện (pcs)</TableCell>
                        <TableCell>TL/ Kiện (kg)</TableCell>
                        <TableCell>S/L Kiện Lẻ 1 (pcs)</TableCell>
                        <TableCell>T/L Kiện Lẻ 1 (kg)</TableCell>
                        <TableCell>S/L Kiện Lẻ 2 (pcs)</TableCell>
                        <TableCell>T/L Kiện Lẻ 2 (kg)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <NumberText
                            value={progress?.packageNumber}
                          ></NumberText>
                        </TableCell>
                        <TableCell>
                          <NumberText
                            value={progress?.packageAmountPerUnit ?? 0}
                          ></NumberText>
                        </TableCell>
                        <TableCell>
                          <NumberText
                            value={progress?.packageWeightPerUnit ?? 0}
                          ></NumberText>
                        </TableCell>
                        <TableCell>
                          <NumberText
                            value={progress?.packageAmount ?? 0}
                          ></NumberText>
                        </TableCell>
                        <TableCell>
                          <NumberText
                            value={progress?.packageWeight ?? 0}
                          ></NumberText>
                        </TableCell>
                        <TableCell>
                          <NumberText
                            value={progress?.packageAmount2 ?? 0}
                          ></NumberText>
                        </TableCell>
                        <TableCell>
                          <NumberText
                            value={progress?.packageWeight2 ?? 0}
                          ></NumberText>
                        </TableCell>
                        <TableCell>{progress?.packageNote ?? ""}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </XGroupCell>
              </XSubRow>
            )}
          </TableBody>
        </XTablePagination>
      </OrderNumberBadge>
    );
  },
);
